import type React from 'react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useUserStore } from '@Auth/store';
import type { Action, PlainAction } from '@components/types';
import { DbSubmissionStatusSchema, NON_PRE_DRAFT_SUBMISSION_STATUSES } from '@domain/schemas/contract.schema';
import type { QueryFilter } from '@domain/schemas/filters.schema';
import { CreateContractButtonController } from '@features/create-contract-button';
import { useBrandingOptions } from '@features/create-entity-modal';
import { deriveQueryFilters, toFilterRows } from '@features/query-builder';
import { useQueryBuilderStore } from '@features/query-builder/store';
import {
  SavedViewActiveHeaderController,
  SavedViewsActionsController,
  useApplySavedViewFilters,
  useSavedViewsEnabled,
} from '@features/saved-views';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useModalDispatch } from '@hooks/useModalProvider';
import { SUBMISSIONS_OPENSEARCH_PAGE_SIZE, useSubmissionsOpensearch } from '@queries/fetchSubmissions';
import { useTags } from '@queries/fetchTags';
import { isNonNullish } from '@root/helpers';
import { isAdmin } from '@root/helpers/permissions/utils';
import { getFormattedStatus } from '@root/helpers/resources';
import withMenuLayout from '@root/src/layout/withMenuLayout';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { useFeatureFlags, useUserTeams } from '@src/hooks';
import type { SortingState } from '@tanstack/react-table';

import { MODALS } from '../../constants';

import { DownloadCSVButton } from './components/DownloadCSVButton';
import { ReactTable } from './components/table/infiniteScroll/ReactTable';
import { getSubmissionsTableColumns } from './helpers/columns/getSubmissionsTableColumns';
import { useOpenSearchFallback } from './hooks/useOpenSearchFallback';
import { useListingPagesStore } from './store/listingPages.store';
import { useDataIsEmpty } from './hooks';

const getInitialFilters = (isPPLFrontend: boolean, hasFirmOrderStatus: boolean): QueryFilter[] => {
  if (isPPLFrontend) return [];
  if (!hasFirmOrderStatus) {
    return [
      {
        field: 'status',
        comparator: 'eq',
        values: NON_PRE_DRAFT_SUBMISSION_STATUSES.filter((s) => !['ARCHIVED', 'FIRM_ORDER'].includes(s)),
      },
    ];
  }
  return [
    { field: 'status', comparator: 'eq', values: NON_PRE_DRAFT_SUBMISSION_STATUSES.filter((s) => s !== 'ARCHIVED') },
  ];
};

const getSortOrder = (sorting?: SortingState) =>
  sorting && sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

export const SubmissionsOpenSearch: React.FC = () => {
  'use memo';

  const user = useUserStore(({ user }) => user);
  const { showModal } = useModalDispatch();
  const { listingPageState, setListingPageState } = useListingPagesStore(
    ({ listingPagesState, setListingPagesState }) => ({
      listingPageState: listingPagesState['contract'],
      setListingPageState: setListingPagesState('contract'),
    }),
  );

  const [search, setSearch] = useState('');
  const handleSearchChange = (search: string) => {
    setListingPageState({ sorting: [] });
    setSearch(search);
  };

  const savedViewsEnabled = useSavedViewsEnabled();

  const getFeatureFlagState = useFeatureFlags();
  const hasFirmOrderStatus = getFeatureFlagState('FirmOrderStatus') ?? false;
  const disableRecordCreation = getFeatureFlagState('DisableRecordCreation') ?? false;
  const canCreate = isPPLFrontend || !disableRecordCreation;

  const initialFilters = getInitialFilters(isPPLFrontend, hasFirmOrderStatus);
  useApplySavedViewFilters(initialFilters);

  // Collapse only on route-level navigation (CB-2993 product decision) — this component doesn't
  // remount in-page. Layout effect: a post-paint close would flash the previously open panel.
  useLayoutEffect(() => {
    useQueryBuilderStore.getState().setPanelOpen('contract', false);
  }, []);

  useEffect(() => {
    if (!useQueryBuilderStore.getState().rowsByEntity['contract']) {
      useQueryBuilderStore.getState().initRows('contract', toFilterRows(initialFilters));
    }
  }, [initialFilters]);

  const queryBuilderRows = useQueryBuilderStore((s) => s.rowsByEntity['contract']);
  const queryFilters = queryBuilderRows ? deriveQueryFilters(queryBuilderRows) : initialFilters;

  const {
    data: submissionsData,
    error,
    errorUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status,
  } = useSubmissionsOpensearch({
    sortField: listingPageState?.sorting?.length > 0 ? listingPageState?.sorting[0]?.id : undefined,
    sortOrder: getSortOrder(listingPageState?.sorting),
    queryFilters,
    search,
  });

  useOpenSearchFallback({ error, errorUpdatedAt });

  const { data: tags = [], isLoading: isTagsLoading } = useTags();
  const { data: teams = [], isLoading: isTeamsLoading } = useUserTeams();
  const { brandingFieldName, data: brandingOptions = [] } = useBrandingOptions();

  const submissions = useMemo(() => submissionsData?.pages?.flatMap((page) => page.data) ?? [], [submissionsData]);
  const totalDBRowCount = submissionsData?.pages[0]?.count ?? 0;
  const totalFetched = submissions.length;

  const firstPage = submissionsData?.pages[0];
  const createdByUsers = firstPage?.createdByUsers ?? [];
  const updatedByUsers = firstPage?.updatedByUsers ?? [];
  const templates = firstPage?.templates ?? [];

  const hasFinalisedContracts = firstPage?.hasFinalisedContracts ?? false;
  const hasPreDrafts = firstPage?.hasPreDrafts ?? false;

  const isEmpty = useDataIsEmpty(submissions, status);

  const hasCommentsFeature = getFeatureFlagState('Comments') ?? false;
  const hasEndorsementsFeature = getFeatureFlagState('Endorsements') ?? false;
  const { pbqaEval, pbqaReview } = useFeatureAccess();
  const hasGuidanceFeature = pbqaEval || pbqaReview;

  // Sync dynamic options (from API aggregations) into the query builder store.
  useEffect(() => {
    useQueryBuilderStore.getState().setOptionsRecord('contract', {
      tags: tags.map((t) => ({ label: t.label, value: t.id })),
      teams: teams.map((t) => ({ label: t.name, value: String(t.id) })),
      branding: brandingOptions.map((b) => ({ label: String(b), value: String(b) })),
      created_by: (firstPage?.createdByUsers ?? []).map((u) => ({ label: u, value: u })),
      last_updated_by: (firstPage?.updatedByUsers ?? []).map((u) => ({ label: u, value: u })),
      sourceResourceDisplayName: (firstPage?.templates ?? []).map((t) => ({ label: t, value: t })),
      status: (hasPreDrafts ? DbSubmissionStatusSchema.options : NON_PRE_DRAFT_SUBMISSION_STATUSES).map((s) => ({
        label: getFormattedStatus(s),
        value: s,
      })),
    });
  }, [tags, teams, brandingOptions, firstPage, hasPreDrafts]);

  const columns = getSubmissionsTableColumns({
    brandingFieldName,
    brandingOptions,
    isAdmin: isAdmin(user),
    disableStatus: isPPLFrontend,
    hasCommentsFeature,
    hasEndorsementsFeature,
    hasFirmOrderStatus,
    hasGuidanceFeature,
    tags,
    teams,
    templates,
    createdByUsers,
    updatedByUsers,
  });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New Contract',
    onClick: () => showModal(MODALS.ADMIN_CREATE_TEMPLATE, { data: submissions }),
  };

  const actions: Action[] = [
    isPPLFrontend && createEntityAction,
    !isPPLFrontend && canCreate && (
      <CreateContractButtonController hasFinalisedContracts={hasFinalisedContracts} key="create-contract-button" />
    ),
    isPPLFrontend ? undefined : <DownloadCSVButton key="download-csv" type="submissions" />,
  ].filter(isNonNullish);

  return (
    <ReactTable
      actions={actions}
      columns={columns}
      columnVisibility={{
        branding_id: false,
        branding: false,
        class_of_business: false,
        commentsCount: false,
        created_at: false,
        created_by: false,
        endorsementsCount: false,
        external_id: false,
        id: false,
        last_updated_by: false,
        missingDatapointsCount: false,
        renewals: false,
        sourceResourceDisplayName: false,
        sourceResourceName: false,
      }}
      columnPinning={{ right: ['view'] }}
      createEntityAction={canCreate ? createEntityAction : undefined}
      entityName="contract"
      filteredData={submissions}
      useQueryBuilder
      queryBuilderHeaderSlot={savedViewsEnabled ? <SavedViewActiveHeaderController /> : undefined}
      queryBuilderFooterSlot={savedViewsEnabled ? <SavedViewsActionsController /> : undefined}
      initialSorting={[{ id: 'created_at', desc: true }]}
      status={status}
      isEmpty={isEmpty}
      isLoading={status === 'pending' || isTagsLoading || isTeamsLoading}
      rowCount={totalDBRowCount}
      handleSearchChange={handleSearchChange}
      searchTerm={search}
      fetchNextPage={fetchNextPage}
      totalFetched={totalFetched}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      pageSize={SUBMISSIONS_OPENSEARCH_PAGE_SIZE}
    />
  );
};

export const UserSubmissionsOpenSearchPage = withMenuLayout(SubmissionsOpenSearch);
